import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import FlightRoundedIcon from '@mui/icons-material/FlightRounded';
import { gamesApi, getSocket, onSocketJoin } from '../../../services/api/gamesApi';
import { authApi } from '../../../services/api/authApi';
import './AviatorScreen.css';

const INITIAL_BETS = [
  { amountInput: '50', autoCashoutInput: '2.00', mode: 'bet', betId: null },
  { amountInput: '400', autoCashoutInput: '2.00', mode: 'bet', betId: null },
];

function getSlotState(index, gameState, playerRows) {
  const myActiveBets = gameState?.myActiveBets ?? [];
  const myQueuedBets = gameState?.myQueuedBets ?? [];
  const active = myActiveBets.find((b) => b.slotIndex === index);
  const queued = myQueuedBets.find((b) => b.slotIndex === index);
  const cashed = playerRows.find((r) => r.isMe && r.isCashedOut && r.slotIndex === index);
  const lost = playerRows.find((r) => r.isMe && !r.cashoutX && r.slotIndex === index);
  return { active, queued, cashed, lost };
}

function AviatorScreen({ user, onBack, onWalletUpdate }) {
  const [gameState, setGameState] = useState(null);
  const [walletBalance, setWalletBalance] = useState(user?.walletBalance ?? 0);
  const [bets, setBets] = useState(INITIAL_BETS);
  const [boardTab, setBoardTab] = useState('all');
  const [toast, setToast] = useState('');
  const [loadingSlot, setLoadingSlot] = useState(null);
  const [lastRoundId, setLastRoundId] = useState(null);

  const refreshUser = useCallback(async () => {
    try {
      const me = await authApi.me();
      setWalletBalance(me.walletBalance);
      onWalletUpdate?.(me);
      authApi.saveSession({ token: localStorage.getItem('eleven_games_token'), user: me });
    } catch {
      /* ignore */
    }
  }, [onWalletUpdate]);

  useEffect(() => {
    let cancelled = false;
    const refreshState = () =>
      gamesApi
        .aviatorState()
        .then((state) => {
          if (!cancelled) setGameState(state);
        })
        .catch(() => {});

    refreshState();
    const pollTimer = setInterval(refreshState, 5000);

    const socket = getSocket();
    const join = () => socket.emit('join:aviator');
    const stopJoin = onSocketJoin(join);
    const onState = (state) => setGameState(state);
    socket.on('aviator:state', onState);

    return () => {
      cancelled = true;
      clearInterval(pollTimer);
      stopJoin();
      socket.off('aviator:state', onState);
    };
  }, []);

  const phase = gameState?.phase ?? 'waiting';
  const multiplier = gameState?.multiplier ?? 1;
  const waitSeconds = gameState?.waitSeconds ?? 5;
  const crashedAt = gameState?.crashedAt;
  const membersCount = gameState?.membersCount ?? 0;
  const maxPlayers = gameState?.maxPlayers ?? 100;
  const queueCount = gameState?.queueCount ?? 0;
  const roundNo = gameState?.roundNo ?? 0;
  const recentCrashPoints = gameState?.recentCrashPoints ?? [];
  const playerRows = useMemo(() => gameState?.bets ?? [], [gameState?.bets]);
  const myActiveBets = gameState?.myActiveBets ?? [];
  const myQueuedBets = gameState?.myQueuedBets ?? [];
  const totalPool = gameState?.totalPool ?? 0;
  const payoutCap = gameState?.payoutCap ?? 0;
  const totalPaidOut = gameState?.totalPaidOut ?? 0;
  const remainingPayout = gameState?.remainingPayout ?? 0;
  const houseCommissionPercent = gameState?.houseCommissionPercent ?? 7;
  const payoutProgress = payoutCap > 0 ? Math.min(100, (totalPaidOut / payoutCap) * 100) : 0;

  useEffect(() => {
    if (!gameState?.roundId) return;
    if (lastRoundId && lastRoundId !== gameState.roundId) {
      setBets(INITIAL_BETS.map((b) => ({ ...b, betId: null })));
    }
    setLastRoundId(gameState.roundId);
  }, [gameState?.roundId, lastRoundId]);

  useEffect(() => {
    setBets((prev) =>
      prev.map((bet, index) => {
        const slot = getSlotState(index, gameState, playerRows);
        const betId = slot.active?.id ?? slot.queued?.id ?? null;
        if (betId) return { ...bet, betId };
        if (phase === 'waiting' && !slot.active && !slot.queued && !slot.cashed) {
          return { ...bet, betId: null };
        }
        return bet;
      }),
    );
  }, [gameState, playerRows, phase]);

  const flightPoint = (() => {
    const m = multiplier;
    const progress = Math.min((m - 1) / 8, 1);
    return { x: 8 + progress * 82, y: 12 + progress * 58 };
  })();

  const curvePoints = [];
  for (let step = 0; step <= 22; step += 1) {
    const t = step / 22;
    const x = Number((flightPoint.x * t).toFixed(2));
    const yFromBottom = 8 + (flightPoint.y - 8) * (t ** 1.6);
    curvePoints.push(`${x},${Number((100 - yFromBottom).toFixed(2))}`);
  }
  const fillPolygon = `0,100 ${curvePoints.join(' ')} ${flightPoint.x},100`;

  const updateBetField = (index, field, value) => {
    setBets((prev) => prev.map((bet, i) => (i === index ? { ...bet, [field]: value } : bet)));
  };

  const hasAnyActiveOrQueued = myActiveBets.length > 0 || myQueuedBets.length > 0;
  const isSpectating = (phase === 'running' || phase === 'crashed') && !hasAnyActiveOrQueued;

  const placeBet = async (index) => {
    const slot = getSlotState(index, gameState, playerRows);
    if (slot.active || slot.queued) {
      setToast(`Panel ${index + 1} already has a bet`);
      return;
    }

    const stake = Number(bets[index].amountInput);
    if (!Number.isFinite(stake) || stake <= 0 || stake > walletBalance) {
      setToast('Invalid bet amount or insufficient balance');
      return;
    }

    setLoadingSlot(index);
    try {
      const autoCashout = bets[index].mode === 'auto' ? Number(bets[index].autoCashoutInput) : undefined;
      const result = await gamesApi.aviatorBet({
        amount: stake,
        slotIndex: index,
        autoCashout: autoCashout > 1 ? autoCashout : undefined,
      });
      setToast(`Panel ${index + 1}: ${result.message}`);
      setBets((prev) =>
        prev.map((b, i) => (i === index ? { ...b, betId: result.betId } : b)),
      );
      await refreshUser();
      setGameState(await gamesApi.aviatorState());
    } catch (err) {
      setToast(err.message);
    } finally {
      setLoadingSlot(null);
    }
  };

  const manualCashOut = async (index) => {
    const slot = getSlotState(index, gameState, playerRows);
    const betId = bets[index].betId || slot.active?.id;
    if (!betId) {
      setToast('No active bet on this panel');
      return;
    }
    setLoadingSlot(index);
    try {
      const result = await gamesApi.aviatorCashout(betId);
      const payout = result.payout ?? Number(((slot.active?.amount ?? Number(bets[index].amountInput)) * result.multiplier).toFixed(2));
      await refreshUser();
      setToast(`Panel ${index + 1}: Won ₹${payout.toFixed(2)} @ ${result.multiplier.toFixed(2)}x`);
      setGameState(await gamesApi.aviatorState());
    } catch (err) {
      setToast(err.message);
    } finally {
      setLoadingSlot(null);
    }
  };

  const visibleRows =
    boardTab === 'previous'
      ? playerRows.filter((row) => row.cashoutX)
      : boardTab === 'top'
        ? [...playerRows].filter((r) => r.cashoutX).sort((a, b) => (b.cashoutX || 0) - (a.cashoutX || 0))
        : playerRows;

  const totalWin = visibleRows.filter((r) => r.winAmount).reduce((s, r) => s + (r.winAmount || 0), 0);
  const cashedUsers = visibleRows.filter((r) => r.cashoutX).length;

  return (
    <Box className="aviator-root">
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
      <Box className="aviator-topbar">
        <IconButton size="small" onClick={onBack} sx={{ color: '#fff' }}>
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <Typography className="aviator-logo">Aviator #{roundNo}</Typography>
        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Chip size="small" label="Live 24/7" sx={{ color: '#fff', bgcolor: 'rgba(76,175,80,0.35)' }} />
          <Chip size="small" label={`${membersCount}/${maxPlayers}`} sx={{ color: '#fff' }} />
          {totalPool > 0 ? (
            <Chip
              size="small"
              label={`Pool left ₹${remainingPayout.toFixed(0)} / ₹${payoutCap.toFixed(0)} (${houseCommissionPercent}% fee)`}
              sx={{
                color: '#fff',
                bgcolor: payoutProgress >= 100 ? 'rgba(244,67,54,0.5)' : 'rgba(76,175,80,0.35)',
              }}
            />
          ) : null}
          {queueCount > 0 ? (
            <Chip size="small" label={`${queueCount} queued`} sx={{ color: '#fff', bgcolor: 'rgba(255,180,0,0.3)' }} />
          ) : null}
          <Chip size="small" label={`Wallet ₹${walletBalance.toFixed(2)}`} sx={{ color: '#fff' }} />
        </Box>
      </Box>

      <Box className="aviator-scroll">
      {myQueuedBets.length > 0 ? (
        <Alert severity="warning" sx={{ mx: 1, mt: 0.5, py: 0 }}>
          {myQueuedBets.length} bet(s) queued for next round.
        </Alert>
      ) : null}

      {phase === 'running' && isSpectating ? (
        <Alert severity="info" sx={{ mx: 1, mt: 0.5, py: 0 }}>
          Round in progress @ {multiplier.toFixed(2)}x — queue bets on either panel for the next round.
        </Alert>
      ) : null}

      {phase === 'running' && myActiveBets.length > 0 ? (
        <Alert severity="success" sx={{ mx: 1, mt: 0.5, py: 0 }}>
          {myActiveBets.length} live bet(s) — cash out each panel separately before crash!
        </Alert>
      ) : null}

      <Box className="aviator-stage">
        <Box className="aviator-rays" />
        <svg className="aviator-graph-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <polygon points={fillPolygon} className="aviator-graph-fill" />
          <polyline points={curvePoints.join(' ')} className="aviator-graph-line" />
        </svg>
        <Box
          className={phase === 'running' ? 'aviator-plane flying' : 'aviator-plane'}
          style={{ left: `${flightPoint.x}%`, bottom: `${flightPoint.y}%` }}
        >
          <FlightRoundedIcon />
        </Box>

        {phase === 'waiting' && (
          <Box className="overlay-box">
            <CircularProgress size={24} thickness={5} />
            <Typography className="overlay-title">Next game starts in {waitSeconds}s</Typography>
            <Typography className="overlay-subtitle">
              {membersCount}/{maxPlayers} players joined
            </Typography>
          </Box>
        )}

        <Typography className={`multiplier ${phase === 'crashed' ? 'crashed' : ''}`}>
          {phase === 'crashed' ? `Crashed @ ${crashedAt}x` : `${multiplier.toFixed(2)}x`}
        </Typography>

        {recentCrashPoints.length > 0 && (
          <Box className="crash-history-row">
            {recentCrashPoints.slice(0, 6).map((pt, i) => (
              <Chip
                key={`crash-${i}-${pt}`}
                size="small"
                label={`${pt}x`}
                sx={{ height: 22, fontSize: '0.7rem', bgcolor: pt < 2 ? '#5c2d2d' : '#2d4a5c', color: '#fff' }}
              />
            ))}
          </Box>
        )}
      </Box>

      <Box className="aviator-controls">
        {bets.map((bet, index) => {
          const slot = getSlotState(index, gameState, playerRows);
          const isLocked = !!(slot.active || slot.queued || slot.cashed);
          const stake = slot.active?.amount ?? Number(bet.amountInput);
          const potentialWin = Number((stake * multiplier).toFixed(2));
          const canCashOut = phase === 'running' && slot.active && !slot.cashed;
          const canPlaceBet = !slot.active && !slot.queued && loadingSlot === null;
          const isLoading = loadingSlot === index;
          const betLabel =
            phase === 'running' || phase === 'crashed'
              ? `Queue ₹${Number(bet.amountInput || 0).toFixed(2)}`
              : `Bet ₹${Number(bet.amountInput || 0).toFixed(2)}`;

          return (
            <Paper elevation={0} className="bet-box" key={`bet-panel-${index}`}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', mb: 0.5, display: 'block' }}>
                Bet Panel {index + 1}
              </Typography>
              <Box className="bet-mode-row">
                <Button
                  size="small"
                  variant={bet.mode === 'bet' ? 'contained' : 'text'}
                  onClick={() => updateBetField(index, 'mode', 'bet')}
                  disabled={isLocked}
                >
                  Bet
                </Button>
                <Button
                  size="small"
                  variant={bet.mode === 'auto' ? 'contained' : 'text'}
                  onClick={() => updateBetField(index, 'mode', 'auto')}
                  disabled={isLocked}
                >
                  Auto
                </Button>
              </Box>
              <TextField
                size="small"
                label="Bet Amount (INR)"
                type="number"
                value={bet.amountInput}
                onChange={(e) => updateBetField(index, 'amountInput', e.target.value)}
                fullWidth
                disabled={isLocked}
              />
              {bet.mode === 'auto' && (
                <TextField
                  size="small"
                  label="Auto Cash Out (x)"
                  type="number"
                  value={bet.autoCashoutInput}
                  onChange={(e) => updateBetField(index, 'autoCashoutInput', e.target.value)}
                  fullWidth
                  disabled={isLocked}
                />
              )}
              <Typography variant="body2" sx={{ minHeight: '20px' }}>
                {slot.cashed
                  ? `Won ₹${(slot.cashed.winAmount || 0).toFixed(2)} @ ${slot.cashed.cashoutX}x`
                  : slot.queued
                    ? `Queued ₹${slot.queued.amount} for next round`
                    : slot.active
                      ? phase === 'running'
                        ? `Live ₹${stake} — tap Cash Out!`
                        : 'Bet placed — waiting for takeoff'
                      : phase === 'waiting'
                        ? 'Set amount & bet'
                        : 'Queue for next round'}
              </Typography>

              {canCashOut ? (
                <Button
                  variant="contained"
                  color="warning"
                  size="large"
                  fullWidth
                  disabled={isLoading}
                  onClick={() => manualCashOut(index)}
                  sx={{ fontWeight: 800, fontSize: '0.95rem', animation: 'pulse 1s infinite' }}
                >
                  Cash Out ₹{potentialWin} @ {multiplier.toFixed(2)}x
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  fullWidth
                  disabled={!canPlaceBet || isLoading}
                  onClick={() => placeBet(index)}
                >
                  {isLoading ? 'Placing…' : betLabel}
                </Button>
              )}
            </Paper>
          );
        })}
      </Box>

      <Paper elevation={0} className="bets-board">
        <Box className="bets-tabs">
          <Button size="small" variant={boardTab === 'all' ? 'contained' : 'text'} onClick={() => setBoardTab('all')}>All Bets</Button>
          <Button size="small" variant={boardTab === 'previous' ? 'contained' : 'text'} onClick={() => setBoardTab('previous')}>Previous</Button>
          <Button size="small" variant={boardTab === 'top' ? 'contained' : 'text'} onClick={() => setBoardTab('top')}>Top</Button>
        </Box>
        <Box className="bets-summary">
          <Box className="avatar-stack">
            {visibleRows.slice(0, 2).map((row) => (
              <Avatar key={row.id} sx={{ width: 28, height: 28 }}>{row.player.slice(0, 1)}</Avatar>
            ))}
          </Box>
          <Typography>{cashedUsers}/{visibleRows.length} Bets</Typography>
          <Typography sx={{ marginLeft: 'auto', fontWeight: 700 }}>₹{totalWin.toFixed(2)}</Typography>
        </Box>
        <Box className="bets-header-row">
          <Typography>Player</Typography>
          <Typography>Bet INR</Typography>
          <Typography>X</Typography>
          <Typography>Win INR</Typography>
        </Box>
        <Box className="bets-list">
          {visibleRows.map((row) => (
            <Box key={row.id} className="bets-list-row">
              <Box className="player-cell">
                <Avatar sx={{ width: 26, height: 26 }}>{row.player.slice(0, 1)}</Avatar>
                <Typography>{row.player}</Typography>
              </Box>
              <Typography>{Number(row.betAmount || 0).toFixed(2)}</Typography>
              <Typography color={row.cashoutX ? '#73f48e' : '#8b92a8'}>{row.cashoutX ? `${row.cashoutX.toFixed(2)}x` : '--'}</Typography>
              <Typography color={row.winAmount ? '#73f48e' : '#8b92a8'}>{row.winAmount ? row.winAmount.toFixed(2) : '--'}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>
      </Box>
    </Box>
  );
}

export default AviatorScreen;
